import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Clock, FileCheck2, Leaf } from "lucide-react";

/**
 * Section « Un mot d'ordre : ANTICIPATION ».
 *
 * Remplace l'ancienne frise Aujourd'hui → J-6 → J-3 → Échéance, qui laissait
 * entendre qu'il s'écoule plusieurs mois entre l'analyse et la signature.
 * C'est l'inverse du message : on anticipe parce que le marché bouge, et on
 * signe dès que les conditions sont bonnes.
 *
 * Mise en page : une colonne sur mobile (le visuel passe sous le texte),
 * deux colonnes à partir de lg. Les cartes flottantes et la colonne de mots
 * à droite sont décoratives — elles n'apparaissent que quand la place existe.
 */

const PILIERS = [
  { icon: BarChart3, titre: "Analyser", suite: "le marché" },
  { icon: Clock, titre: "Anticiper", suite: "le bon moment" },
  { icon: FileCheck2, titre: "Sécuriser", suite: "votre contrat" },
  { icon: Leaf, titre: "Profiter", suite: "d'un prix optimisé" },
];

export default function AnticiperTimeline() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      className="relative overflow-hidden text-primary-foreground"
      style={{ background: "linear-gradient(135deg, #04140e 0%, #0a2e21 48%, #061c14 100%)" }}
    >
      {/* Arcs de fond — purement décoratifs, ils suivent la largeur du bloc. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <span
          className="absolute -right-[18%] -top-[30%] h-[150%] w-[75%] rounded-full border border-accent/20"
          style={{ background: "radial-gradient(closest-side, rgba(196,240,66,0.07), transparent 70%)" }}
        />
        <span className="absolute -bottom-[45%] -right-[10%] h-[90%] w-[120%] rounded-[50%] border border-white/[0.07]" />
        <span
          className="absolute left-1/2 top-0 h-full w-px bg-gradient-to-b from-transparent via-accent/20 to-transparent hidden xl:block"
          style={{ left: "62%" }}
        />
      </div>

      <div
        ref={ref}
        className={`em-container relative py-16 md:py-20 lg:flex lg:min-h-[100svh] lg:flex-col lg:justify-center lg:py-[clamp(3rem,6.5vh,4.5rem)] transition-all duration-700 ease-out ${
          shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="grid items-center gap-10 sm:gap-12 lg:min-h-0 lg:flex-1 lg:items-stretch lg:grid-cols-[minmax(0,1fr)_minmax(0,0.72fr)] lg:gap-x-10 lg:gap-y-0 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] xl:gap-12 2xl:gap-16">

          {/* ── Colonne texte ── */}
          <div className="lg:flex lg:flex-col lg:justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-1.5">
              <span aria-hidden className="h-2 w-2 rounded-full bg-accent" />
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-accent">Anticipation</span>
            </span>

            <h2 className="font-display mt-5 lg:mt-[clamp(0.75rem,2vh,1.25rem)] text-[clamp(1.75rem,4vw,2.75rem)] font-bold leading-[1.08] tracking-tight">
              Un mot d'ordre :
              <span className="mt-1 block font-black text-accent">ANTICIPATION</span>
            </h2>

            <p className="mt-5 max-w-[62ch] lg:mt-[clamp(0.75rem,2vh,1.25rem)] text-base leading-relaxed text-white/80 lg:text-lg xl:max-w-xl">
              Un contrat d'énergie peut être signé jusqu'à deux ans avant son échéance. Anticiper ne consiste pas à
              attendre : dès l'analyse terminée, nous signons au plus vite, tant que les conditions sont bonnes.
            </p>

            {/* Les quatre piliers — 2 colonnes sur mobile, 4 en ligne dès sm */}
            <ul className="mt-8 grid grid-cols-2 gap-x-5 gap-y-7 sm:mt-10 lg:mt-[clamp(1.25rem,3.2vh,2rem)] sm:grid-cols-4 sm:gap-x-0">
              {PILIERS.map(({ icon: Icon, titre, suite }, i) => (
                <li
                  key={titre}
                  className={`sm:px-3 sm:first:pl-0 xl:px-4 ${i ? "sm:border-l sm:border-white/15" : ""}`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-accent/45 bg-accent/10 sm:h-11 sm:w-11">
                    <Icon className="h-5 w-5 text-accent" strokeWidth={1.9} aria-hidden />
                  </span>
                  <p className="mt-3 font-display text-sm font-bold leading-tight sm:mt-4 xl:text-[15px]">{titre}</p>
                  <p className="mt-0.5 text-[13px] leading-snug text-white/65 xl:text-sm">{suite}</p>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6 lg:mt-[clamp(1.25rem,3.2vh,2rem)]">
              <Link
                to="/contact"
                className="group inline-flex h-14 w-full items-center justify-center gap-3 rounded-full bg-accent px-7 font-bold text-accent-foreground transition-colors hover:bg-accent/90 sm:w-auto sm:justify-start sm:pl-8 sm:pr-6"
              >
                Parler à un expert
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
              </Link>
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/65">
                <span>Étude gratuite</span>
                <span aria-hidden className="h-1 w-1 rounded-full bg-accent/70" />
                <span>Sans engagement</span>
              </p>
            </div>
          </div>

          {/* ── Colonne visuelle ── */}
          <div className="relative lg:flex">
            <div className="relative mx-auto flex w-full max-w-[520px] flex-col lg:mx-0 lg:my-auto lg:h-full lg:max-h-[640px] lg:max-w-none">
              <div className="overflow-hidden rounded-[2rem] border border-white/10 shadow-2xl lg:h-full">
                <picture>
                  <source srcSet="/img/anticipation-pro.webp" type="image/webp" />
                  <img
                    src="/img/anticipation-pro.jpg"
                    alt="Immeuble de bureaux contemporain équipé de panneaux photovoltaïques en toiture"
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/3] w-full object-cover lg:aspect-auto lg:h-full lg:min-h-[280px] lg:max-h-[640px]"
                  />
                </picture>
              </div>

              {/* Carte « bon moment » — masquée sous sm, elle mangerait la photo */}
              <div className="absolute -top-5 right-0 hidden w-[200px] lg:top-6 rounded-2xl border border-white/20 bg-[#0A2418]/70 p-4 shadow-xl backdrop-blur-md md:block lg:hidden xl:block xl:-right-6 xl:w-[215px]">
                <p className="text-sm font-semibold leading-tight">Le bon moment</p>
                <p className="text-sm leading-tight text-white/75">au bon prix</p>
                <svg viewBox="0 0 160 46" className="mt-3 w-full" fill="none" aria-hidden>
                  <path
                    d="M2 38 L24 30 L42 34 L60 20 L78 26 L96 14 L118 18 L138 8 L156 4"
                    stroke="#C4F042"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="156" cy="4" r="4" fill="#C4F042" />
                </svg>
              </div>

              {/* Carte « économies durables » */}
              <div className="absolute -bottom-6 left-0 hidden items-center gap-3 lg:bottom-6 rounded-2xl border border-white/20 bg-[#0A2418]/70 p-4 shadow-xl backdrop-blur-md md:flex lg:hidden xl:flex xl:-left-8">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-accent/45 bg-accent/10">
                  <Leaf className="h-5 w-5 text-accent" strokeWidth={1.9} aria-hidden />
                </span>
                <span className="text-sm leading-tight">
                  Aujourd'hui,
                  <span className="block text-white/75">des économies durables</span>
                </span>
              </div>
            </div>

            {/* Colonne de mots — décorative, seulement quand il y a la place */}
            <ul aria-hidden className="pointer-events-none absolute right-0 top-1/2 hidden -translate-y-1/2 translate-x-[calc(100%+1.25rem)] space-y-4 text-right 2xl:block">
              {PILIERS.map((p) => (
                <li key={p.titre} className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/45">
                  {p.titre}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Pied de section ── */}
        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 sm:mt-14 lg:mt-[clamp(1.5rem,3.5vh,2.5rem)] md:flex-row md:items-center md:justify-between">
          <p className="flex items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45 sm:tracking-[0.24em]">
            <span aria-hidden className="hidden h-px w-10 bg-white/25 md:block" />
            Des décisions d'aujourd'hui pour un avenir plus durable
          </p>
          <p className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45 sm:tracking-[0.24em] md:justify-end">
            L'énergie à un meilleur avenir
            <span aria-hidden className="hidden h-px w-10 bg-accent/60 md:block" />
          </p>
        </div>
      </div>
    </section>
  );
}
